/**
 * Jedno zrodlo prawdy o statusie analizy.
 *
 * Powstalo, bo mapa etykiet byla zduplikowana w SessionsPage i SessionPage i zdazyla
 * sie rozjechac: lista znala komplet dziewieciu statusow, szczegol tylko siedem
 * (brakowalo PROBING i LENSES, wiec fallback wyswietlal surowy kod z bazy), a wspolne
 * statusy mialy w obu plikach inne brzmienie.
 *
 * Komplet wartosci pochodzi z komentarza przy `status` w prisma/schema.prisma - to
 * String, nie enum, wiec nic go po stronie bazy nie pilnuje. Pilnuje go test
 * tests/unit/sessionStatus.test.ts.
 */

import { STATUS_CZEKA_NA_CZLOWIEKA, STATUSY_SESJI, type InputKind } from "@shared/ocenaTypy.js";

/**
 * Wszystkie dopuszczalne statusy, w kolejnosci cyklu zycia. Lista zyje w
 * shared/ocenaTypy.ts (STATUSY_SESJI), zeby serwer i klient nie mieli dwoch kopii;
 * tam tez jest opis SPLITTING i SPLIT_REVIEW (tylko tekst bez podpisow mowcow).
 */
export const WSZYSTKIE_STATUSY = STATUSY_SESJI;

export type SessionStatus = (typeof WSZYSTKIE_STATUSY)[number];

/**
 * Statusy, przy ktorych analiza jeszcze trwa - sesja nie jest skonczona.
 *
 * SPLIT_REVIEW tez tu jest, choc nic sie wtedy nie liczy: sesja czeka na zatwierdzenie
 * podzialu przez czlowieka. Ekrany, ktore pod IN_PROGRESS pokazuja pasek postepu (lista
 * sesji), musza ten stan obsluzyc osobno, a odpytywanie serwera ma go pomijac
 * (patrz `czyOdpytywac`).
 */
export const IN_PROGRESS: ReadonlySet<string> = new Set([
  "QUEUED",
  "PROBING",
  "TRANSCRIBING",
  "PROSODY",
  "EMOTIONS",
  "SPLITTING",
  "SPLIT_REVIEW",
  "LENSES",
]);

/** Czy sesja czeka na zatwierdzenie podzialu tekstu (nic sie wtedy nie liczy). */
export function czekaNaCzlowieka(status: string): boolean {
  return status === STATUS_CZEKA_NA_CZLOWIEKA;
}

/**
 * Czy widok ma odpytywac serwer o ten status. Trwajaca praca tak; stan koncowy nie, i
 * czekanie na zatwierdzenie podzialu tez nie: sam sie nie zmieni, zmieni go klikniecie,
 * po ktorym widok pobiera sesje jeszcze raz.
 */
export function czyOdpytywac(status: string): boolean {
  return IN_PROGRESS.has(status) && !czekaNaCzlowieka(status);
}

/**
 * Etykiety statusu po polsku (CP-8).
 *
 * "LENSES" celowo NIE nazywa sie "soczewki" - to wewnetrzna nazwa etapu pipeline'u,
 * ktora dla odbiorcy nie znaczy nic (CP-9: jedna rzecz = jedna nazwa, i to nazwa
 * z jezyka uzytkownika).
 */
export const STATUS_LABEL: Record<string, string> = {
  QUEUED: "w kolejce",
  PROBING: "sprawdzanie pliku",
  TRANSCRIBING: "rozpoznawanie mowy",
  PROSODY: "pomiar mowy",
  EMOTIONS: "ocena brzmienia",
  SPLITTING: "podział tekstu na wypowiedzi",
  SPLIT_REVIEW: "czeka na zatwierdzenie podziału",
  LENSES: "ocena wg metodyki",
  DONE: "gotowe",
  NO_SPEECH: "nie znaleziono mowy",
  FAILED: "błąd",
};

export function etykietaStatusu(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

/**
 * Tor postepu zalezy od tego, co weszlo do analizy:
 *
 *   nagranie         - pieciu krokow od sprawdzenia pliku do oceny wg metodyki,
 *   tekst-podpisany  - tekst z podpisami mowcow: mowcy i wypowiedzi sa od razu, zostaje ocena,
 *   tekst-podzial    - tekst bez podpisow: model dzieli go na wypowiedzi, uzytkownik zatwierdza
 *                      podzial, dopiero potem ocena.
 */
export type RodzajToru = "nagranie" | "tekst-podpisany" | "tekst-podzial";

/**
 * Tor dla sesji. Przy tekscie rodzaj wynika z tego, co o sesji wiemy: status SPLITTING i
 * SPLIT_REVIEW oraz oznaczenie "mowcow przypisala AI" mowia, ze byl podzial; sesja w kolejce
 * albo nieudana, ktora nie ma jeszcze wypowiedzi, czeka na podzial (albo na nim padla).
 */
export function rodzajToru(sesja: {
  inputKind: InputKind;
  status: string;
  speakersAssignedBy?: string | null;
  /** Czy sesja ma juz zapisane wypowiedzi (utterances.length > 0 albo utteranceCount > 0). */
  maWypowiedzi: boolean;
}): RodzajToru {
  if (sesja.inputKind !== "TEXT") return "nagranie";
  if (sesja.status === "SPLITTING" || sesja.status === STATUS_CZEKA_NA_CZLOWIEKA) return "tekst-podzial";
  if (sesja.speakersAssignedBy === "AI") return "tekst-podzial";
  return sesja.maWypowiedzi ? "tekst-podpisany" : "tekst-podzial";
}

interface DefinicjaKroku {
  status: string;
  etykieta: string;
}

/**
 * Kroki pipeline'u PP-1 dla nagrania. Piec, nie szesc: QUEUED to brak czynnosci, wiec renderuje sie
 * jako "wszystkie kolka oczekuja", a nie jako etap zrobiony.
 *
 * "Sprawdzenie pliku" (PROBING) NIE zapala sie nigdy i ma na to powod, ktory nalezy znac, zanim
 * ktos "naprawi" je ustawieniem statusu: sondowanie ffprobe dzieje sie SYNCHRONICZNIE PRZY WGRANIU,
 * w src/routes/sessionsApi.ts, zanim powstanie zadanie i zanim klient pozna identyfikator sesji.
 * Ustawienie tam statusu PROBING byloby zapisem, ktorego nikt nigdy nie odczyta. Przeniesienie
 * sondowania do workera zapaliloby kolko, ale kosztem odrzucania plikow bez sciezki audio kodem 415
 * od razu - a to jest lepsze zachowanie niz awaria minute pozniej. Kolko zostaje jako zapowiedz etapu.
 */
export const KROKI_PIPELINE = [
  { status: "PROBING", etykieta: "Sprawdzenie pliku" },
  { status: "TRANSCRIBING", etykieta: "Rozpoznanie mowy" },
  { status: "PROSODY", etykieta: "Pomiar mowy" },
  { status: "EMOTIONS", etykieta: "Brzmienie" },
  { status: "LENSES", etykieta: "Metodyka" },
] as const;

/**
 * Kroki toru tekstowego. W odroznieniu od nagrania QUEUED jest tu krokiem ("Przyjęcie tekstu"):
 * tekst nie ma etapow przed kolejka, wiec czekanie na worker to jedyna czynnosc, jaka widac.
 * SPLIT_REVIEW to krok "Zatwierdzenie podziału" - w toku znaczy "czeka na Ciebie".
 */
const KROKI_TEKSTU_PODPISANEGO: readonly DefinicjaKroku[] = [
  { status: "QUEUED", etykieta: "Przyjęcie tekstu" },
  { status: "LENSES", etykieta: "Ocena wg metodyki" },
];

const KROKI_TEKSTU_Z_PODZIALEM: readonly DefinicjaKroku[] = [
  { status: "QUEUED", etykieta: "Przyjęcie tekstu" },
  { status: "SPLITTING", etykieta: "Podział na wypowiedzi" },
  { status: "SPLIT_REVIEW", etykieta: "Zatwierdzenie podziału" },
  { status: "LENSES", etykieta: "Ocena wg metodyki" },
];

export function definicjeKrokow(tor: RodzajToru): readonly DefinicjaKroku[] {
  if (tor === "tekst-podpisany") return KROKI_TEKSTU_PODPISANEGO;
  if (tor === "tekst-podzial") return KROKI_TEKSTU_Z_PODZIALEM;
  return KROKI_PIPELINE;
}

/** Podpis toru nad etapami (czytnik ekranu), zalezny od rodzaju wejscia. */
export function etykietaToru(tor: RodzajToru): string {
  return tor === "nagranie" ? "Etapy analizy nagrania" : "Etapy analizy tekstu";
}

export type StanKroku = "oczekuje" | "w-toku" | "zrobione" | "blad";

export interface KrokPipeline {
  status: string;
  etykieta: string;
  stan: StanKroku;
}

/**
 * Stan kazdego kolka dla podanego statusu sesji (domyslnie tor nagrania).
 *
 * FAILED nie niesie informacji, KTORY etap padl - baza trzyma tylko `error` i `errorKind`,
 * a status nadpisuje poprzedni. Nie zgadujemy wiec winnego kroku (to ta sama zasada, co
 * "nie pokazujemy liczby, ktorej nie ufamy"): caly tor idzie w stan bledu, a nazwe
 * problemu niesie blok S-9 w karcie, zasilony polem `error`.
 */
export function krokiPipeline(status: string, tor: RodzajToru = "nagranie"): KrokPipeline[] {
  const definicje = definicjeKrokow(tor);
  const indeks = definicje.findIndex((k) => k.status === status);
  const zakonczona = status === "DONE" || status === "NO_SPEECH";

  return definicje.map((krok, i) => {
    let stan: StanKroku;
    if (zakonczona) {
      stan = "zrobione";
    } else if (status === "FAILED") {
      stan = "blad";
    } else if (indeks === -1) {
      // QUEUED nagrania albo status nieznany - nic sie jeszcze nie dzieje.
      stan = "oczekuje";
    } else if (i < indeks) {
      stan = "zrobione";
    } else if (i === indeks) {
      stan = "w-toku";
    } else {
      stan = "oczekuje";
    }
    return { status: krok.status, etykieta: krok.etykieta, stan };
  });
}

/**
 * Licznik do miniaturowego toru postepu na liscie: "2/5".
 *
 * Dla `DONE` zwraca komplet. Tor renderuje sie jednak WYLACZNIE dla statusow w toku (lista)
 * i znika razem z karta przebiegu po zakonczeniu (PR-4). Pilnuje tego test "tor postepu
 * pokazuje sie wylacznie w trakcie analizy". Nagranie wgrane przez stary klient (bez wyboru
 * parametrow) nie przechodzi etapu oceny, a DONE i tak liczy je jako komplet - dlatego licznik
 * przy gotowej analizie nigdy nie jest pokazywany.
 */
export function postepPipeline(
  status: string,
  tor: RodzajToru = "nagranie",
): { zrobione: number; wszystkie: number } {
  const kroki = krokiPipeline(status, tor);
  return {
    zrobione: kroki.filter((k) => k.stan === "zrobione").length,
    wszystkie: kroki.length,
  };
}

/**
 * Podpis do paska postepu na liscie: "Krok 2 z 5".
 *
 * Numerem jest krok BIEZACY, a nie liczba ukonczonych: dopoki nic nie zakonczono (analiza w
 * kolejce), ukonczonych jest 0, a "Krok 0 z 5" nie istnieje. Biezacy krok to ukonczone + 1,
 * ale nigdy wiecej niz wszystkie (komplet po DONE). Sam pasek dalej idzie po ukonczonych
 * (`postepPipeline`), bo szerokosc wypelnienia ma mowic, ile juz zrobiono.
 */
export function podpisKroku(status: string, tor: RodzajToru = "nagranie"): string {
  const { zrobione, wszystkie } = postepPipeline(status, tor);
  return `Krok ${Math.min(zrobione + 1, wszystkie)} z ${wszystkie}`;
}
